import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';
import { useSelector } from 'react-redux';
import { toTitleCase } from 'ontraccr-common/lib/Common';
import { TaskHelpers } from 'ontraccr-common';

import { TIME_TYPE_FLAG } from '../../../../constants/Flags';
import TypeOfTimePicker from './TypeOfTimePicker';
import OnTraccrTimePicker from '../../../../common/inputs/OnTraccrTimePicker';

const TIME_FORMAT = 'hh:mm a';

const getValidStartOfDay = (timeValue, timezone) => {
  if (timeValue) {
    const dt = DateTime.fromMillis(timeValue, { zone: timezone });
    if (dt.isValid) {
      return dt.startOf('day');
    }
  }
  return null;
};

/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/jsx-no-useless-fragment */
export default function TimeEntryTableStartEndTimeInput(props) {
  const {
    hourBased,
    date,
    modifier,
    timezone,
    value,
    onChange,
    onConfirm,
  } = props;

  const [type, setType] = useState('regular');
  const [timePickerOpen, setTimePickerOpen] = useState(false);

  const { paidFlags = [] } = useSelector((state) => state.settings.company);
  const { settings = {} } = useSelector((state) => state.settings.company);

  const {
    enableManualOT = false,
    showOriginalTimes,
    minuteIncrement = 1,
    useEndDateOvernight,
  } = settings;

  const hasTimeTypeFlag = useMemo(() => paidFlags.includes(TIME_TYPE_FLAG), [paidFlags]);
  const safeDate = useMemo(() => {
    // HARBOUR-7909: Allow overnight times
    if (modifier === 'start' && value && useEndDateOvernight) {
      const startTimeValue = type === 'regular' && showOriginalTimes ? value.originalStart : value.startTime;
      const startOfDay = getValidStartOfDay(startTimeValue, timezone);
      if (startOfDay) return startOfDay;
    } else if (modifier === 'end' && value) {
      const endTimeValue = type === 'regular' && showOriginalTimes ? value.originalEnd : value.endTime;
      const startOfDay = getValidStartOfDay(endTimeValue, timezone);
      if (startOfDay) return startOfDay;
    }
    // Otherwise, use the provided date prop
    return date.isValid ? date : DateTime.local();
  }, [
    date,
    modifier,
    value,
    type,
    showOriginalTimes,
    timezone,
    useEndDateOvernight,
  ]);

  const { timeKey, oppositeKey } = useMemo(() => {
    if (type === 'regular') {
      return {
        timeKey: `${modifier}Time`,
        oppositeKey: `${modifier === 'start' ? 'end' : 'start'}Time`,
      };
    }

    return {
      timeKey: `${type}${toTitleCase(modifier)}Time`,
      oppositeKey: `${type}${toTitleCase(modifier === 'start' ? 'end' : 'start')}Time`,
    };
  }, [type, modifier]);

  const millisValue = useMemo(() => {
    const getDisplayFunc = modifier === 'start' ? TaskHelpers.getDisplayStart : TaskHelpers.getDisplayEnd;
    const millis = type === 'regular' && showOriginalTimes
      ? getDisplayFunc(value, showOriginalTimes)
      : value[timeKey];

    return millis ?? null;
  }, [type, showOriginalTimes, value, timeKey, modifier]);

  const onTimeChange = (newTime) => {
    if (!newTime) return;
    const dt = DateTime.fromMillis(newTime);
    const baseDate = date.isValid ? date : DateTime.local();

    // Always anchor the new time to the base entry date (day0).
    // safeDate may be day+1 for overnight end times, but we re-evaluate
    // overnight after placing both times on day0.
    const newTimeMillis = baseDate
      .setZone(timezone)
      .set({
        hour: dt.hour, minute: dt.minute, second: 0, millisecond: 0,
      }).toMillis();

    const newValue = {
      ...value,
      [timeKey]: newTimeMillis,
    };

    // HARBOUR-6042: Set opposite time key if it is not set
    /* HARBOUR-8372: Removed the logic added for HARBOUR-6042
                     because we no longer support zero ms entries
    */

    if (type === 'regular') {
      const originalKey = modifier === 'start' ? 'originalStart' : 'originalEnd';
      const oppositeOriginalKey = modifier === 'start' ? 'originalEnd' : 'originalStart';
      newValue[originalKey] = newTimeMillis;

      // Restore opposite time from its original value, stripping any +1 day offset
      // that may have been applied by a prior overnight shift
      const oppositeRaw = newValue[oppositeOriginalKey] ?? newValue[oppositeKey];
      if (oppositeRaw) {
        const oppositeDt = DateTime.fromMillis(oppositeRaw, { zone: timezone });
        newValue[oppositeKey] = baseDate.setZone(timezone).set({
          hour: oppositeDt.hour,
          minute: oppositeDt.minute,
          second: 0,
          millisecond: 0,
        }).toMillis();
      }

      // Both times are now on day0 - re-apply overnight shift if still needed
      if (newValue.startTime && newValue.endTime && newValue.endTime < newValue.startTime) {
        const shifted = TaskHelpers.shiftOvernightTimes({
          ...newValue,
          date: baseDate,
          timezone,
        }, !useEndDateOvernight);
        newValue.startTime = shifted.startTime;
        newValue.endTime = shifted.endTime;
        newValue.originalStart = shifted.originalStart;
        newValue.originalEnd = shifted.originalEnd;
      }
    }

    onChange(newValue);
  };

  const InputComponent = hourBased ? (
    <></>
  ) : (
    <OnTraccrTimePicker
      {...props}
      date={safeDate}
      timezone={timezone}
      format={TIME_FORMAT}
      value={millisValue}
      open={timePickerOpen}
      onChange={onTimeChange}
      onOpenChange={(open) => {
        setTimePickerOpen(open);
        if (!open) {
          onConfirm();
        }
      }}
      allowClear={false}
      minuteIncrement={minuteIncrement}
    />
  );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        gap: 5,
      }}
    >
      {!hourBased && !!hasTimeTypeFlag && (
        <TypeOfTimePicker type={type} setType={setType} enableManualOT={enableManualOT} />
      )}
      {InputComponent}
    </div>
  );
}

/* eslint-disable react/forbid-prop-types */
TimeEntryTableStartEndTimeInput.propTypes = {
  date: PropTypes.instanceOf(DateTime).isRequired,
  timezone: PropTypes.string,
  hourBased: PropTypes.bool,
  value: PropTypes.object,
  modifier: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

TimeEntryTableStartEndTimeInput.defaultProps = {
  hourBased: false,
  value: {},
  timezone: DateTime.local().zoneName,
};
